// Paywall and payment utilities

const STORAGE_KEY = 'expense_tracker_premium';
const DEMO_EXPENSE_LIMIT = 5;

export interface PremiumStatus {
  isPremium: boolean;
  purchaseDate?: string;
  stripeCustomerId?: string;
}

export function getPremiumStatus(): PremiumStatus {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Silently fail and return default
  }
  return { isPremium: false };
}

export function setPremiumStatus(status: PremiumStatus): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(status));
  } catch (e) {
    // Silently fail
  }
}

export function canAddExpense(currentExpenseCount: number): boolean {
  const status = getPremiumStatus();
  if (status.isPremium) {
    return true;
  }
  return currentExpenseCount < DEMO_EXPENSE_LIMIT;
}

export function getExpenseLimitMessage(currentExpenseCount: number): string | null {
  const status = getPremiumStatus();
  if (status.isPremium) {
    return null;
  }
  
  const remaining = DEMO_EXPENSE_LIMIT - currentExpenseCount;
  if (remaining <= 0) {
    return 'Demo limit reached! Upgrade to add unlimited expenses.';
  } else if (remaining <= 2) {
    return `${remaining} free expense${remaining === 1 ? '' : 's'} remaining. Upgrade for unlimited access!`;
  }
  return null;
}

// Stripe integration - Replace with your production key before launch
export const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_YOUR_KEY_HERE';
export const PRODUCT_PRICE = 5.99;

export async function createCheckoutSession(): Promise<string | null> {
  try {
    // Backend creates the checkout session
    const response = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        product: 'expense-tracker-premium',
        price: PRODUCT_PRICE,
      }),
    });

    const { sessionId } = await response.json();
    return sessionId;
  } catch (error) {
    return null;
  }
}

// For MVP, we'll use a simple unlock code system alongside Stripe
const UNLOCK_CODES = [
  'EARLYBIRD2025',
  'TAXSAVER2025',
  'OWNER2025' // Owner access code
];

export function validateUnlockCode(code: string): boolean {
  return UNLOCK_CODES.includes(code.toUpperCase());
}

export function unlockWithCode(code: string): boolean {
  if (validateUnlockCode(code)) {
    setPremiumStatus({
      isPremium: true,
      purchaseDate: new Date().toISOString(),
      stripeCustomerId: `code_${code.toUpperCase()}`
    });
    return true;
  }
  return false;
}
